import React from 'react';
import {Pressable, StyleSheet, View} from 'react-native';

import Deliver from '../images/brand/svg/icons/Delivery_icon_constrained.svg';
import Instore from '../images/brand/svg/icons/In_Store_icon_constrained.svg';
import Pickup from '../images/brand/svg/icons/Pick_UP_icon_constrained.svg';
import {Shop} from '../models/middleware';
import theme from '../style/theme';
import {renderAddressLines} from '../utils/address';

import Text from './ui-controls/Text';

const makeOpenTodayTodayTextForDay = (shop, dayName) => {
  const openTime = shop.regular_hours?.[dayName]?.from;
  const closeTime = shop.regular_hours?.[dayName]?.until;

  if (!openTime || !closeTime) {
    return null;
  }

  const isFormatHHMMSS = openTime?.split(':').length === 3;
  if (isFormatHHMMSS) {
    const formatTime = time => {
      // Assuming time is in 'HH:MM:SS' format
      const [hours, minutes] = time.split(':');
      const date = new Date();
      date.setHours(hours, minutes, 0); // Set seconds to 0
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      });
    };

    const formattedOpenTime = formatTime(openTime);
    const formattedCloseTime = formatTime(closeTime);

    return `Open today ${formattedOpenTime} - ${formattedCloseTime}`;
  } else {
    return `Open today ${openTime} - ${closeTime}`;
  }
};

interface StoreItemProps {
  item: Shop;
  dayName: string;
  selectStore?: (item: Shop) => void;
}

const StoreItem = ({item, dayName, selectStore}: StoreItemProps) => {
  const openTodayText = makeOpenTodayTodayTextForDay(item, dayName);

  const Content = (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.storeName}>{item.name}</Text>
        <Text style={styles.distanceText}>{item.distance?.toFixed(1)} KM</Text>
      </View>
      <View style={styles.storeTile}>
        <View style={styles.storeTileLeft}>
          <View>
            <Text style={styles.storeTileAddressText}>
              {renderAddressLines(item?.address?.lines)}
            </Text>
            <Text style={styles.storeTileAddressLocalityText}>
              {item?.address?.locality}, {item?.address?.administrative_area?.toUpperCase()}{' '}
              {item?.address?.postal_code}
            </Text>
          </View>
          {!!openTodayText && (
            <Text style={[styles.distanceText, {marginTop: 4}]}>{openTodayText}</Text>
          )}
        </View>
        <View style={styles.storeTileRight}>
          {!!item.delivery_available && <Deliver width={40} height={40} />}
          {!!item.pickup_available && <Pickup width={40} height={40} style={{marginLeft: 4}} />}
          {!item.delivery_available && !item.pickup_available && (
            <Instore width={40} height={40} style={{marginLeft: 4}} />
          )}
        </View>
      </View>
      <View style={{marginTop: 8}} />
    </View>
  );

  return selectStore ? (
    <Pressable onPress={() => selectStore(item)} testID="store-list-item">
      {Content}
    </Pressable>
  ) : (
    <View>{Content}</View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: 4,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  storeName: {
    fontSize: 16,
    color: theme.colour.text.heading.primary,
    fontWeight: '600',
    marginBottom: 2,
    textTransform: 'capitalize',
    flexWrap: 'wrap',
  },
  distanceText: {
    fontSize: 12,
    color: theme.colour.text.heading.primary,
    fontWeight: '400',
  },
  storeTile: {
    flexDirection: 'row',
  },
  storeTileLeft: {
    flex: 1,
  },
  storeTileAddressText: {
    fontSize: 12,
    textTransform: 'capitalize',
  },
  storeTileAddressLocalityText: {
    fontSize: 12,
  },
  storeTileRight: {
    justifyContent: 'flex-end',
    alignItems: 'center',
    flexDirection: 'row',
    marginLeft: 8,
  },
});

export default StoreItem;
